import { Dispatch, FC, HTMLAttributes, SetStateAction, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { ResponsiveImage, Trash, TypeStyle, Warning } from '@syconium/little-miss-figgy'

import * as InformationalDialog from '../../../app/_components/dialogs/InformationalDialog/InformationalDialog.client'
import { NextDialogProvider } from '../../../app/_components/dialogs/NextDialog.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { PriceAndDeleteButton } from '../../pages/cart/CartItem/PriceAndDeleteButton/PriceAndDeleteButton'
import {
  DeleteIconButton,
  DeleteIconColumn,
} from '../../pages/cart/CartItem/PriceAndDeleteButton/styles'

import { DetailLine, DigitizationSneakPeekVisual } from './styles'
import { Template } from './Template'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  imgSrc: string | undefined
  logoName: string
  logoPlacement: string | undefined
  logoColor: string | undefined
  logoNote: string | undefined
  effectivePrice: number | undefined
  fullPrice: number | undefined
  title: string
  // Omit for a read-only line (order history)
  removeItem?: () => void
}

interface DialogProps extends HTMLAttributes<HTMLDivElement> {
  removeItem: () => void
  isDialogOpen: boolean
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>
}

// Dialog to alert users that removing digitization will remove corresponding embroidery items
const ConfirmDeleteDialog: FC<DialogProps> = ({ removeItem, isDialogOpen, setIsDialogOpen }) => {
  const closeDialog = () => setIsDialogOpen(false)
  const {
    embroidery: {
      modal: { digitizationLineItemModal: strings },
    },
  } = useTranslation()
  return (
    <NextDialogProvider>
      <StyledInformationalDialog
        analyticsTitle='Logo Digitization Confirm Delete'
        variant='default'
        onClose={closeDialog}
        open={isDialogOpen}
      >
        <StyledInformationalDialogHeader closeText='dialog'>
          <StyledInformationalDialogTitle>
            <StyledWarning />
            {strings.title}
          </StyledInformationalDialogTitle>
        </StyledInformationalDialogHeader>
        <StyledInformationalDialogBody>{strings.body}</StyledInformationalDialogBody>
        <StyledInformationalDialogControls>
          <StyledInformationalDialogButton variant='secondary' onClick={closeDialog}>
            {strings.cancelCta}
          </StyledInformationalDialogButton>
          <StyledInformationalDialogButton onClick={removeItem}>
            {strings.removeCta}
          </StyledInformationalDialogButton>
        </StyledInformationalDialogControls>
      </StyledInformationalDialog>
    </NextDialogProvider>
  )
}

// This sneakpeek is specifically for the cart as a separate line item
export const DigitizationSneakPeek: FC<Props> = ({
  imgSrc,
  logoName,
  logoPlacement,
  logoColor,
  logoNote,
  effectivePrice,
  fullPrice,
  title,
  removeItem,
  ...rest
}) => {
  const {
    embroidery: {
      modal: { forms: strings },
    },
    cart: { removeItemLabel },
  } = useTranslation()
  const removeItemAriaLabel = removeItemLabel.replace('{{itemTitle}}', title)
  const logoLabel = strings.logoOption.logoLabelLine.replace('{{logoName}}', logoName)
  const logoPlacementLabel = strings.logoOption.logoPlacementLabelLine.replace(
    '{{logoPlacement}}',
    logoPlacement ?? ''
  )
  const logoNoteLabel = strings.logoOption.logoNoteLabelLine.replace(
    '{{logoNote}}',
    logoNote ? `"${logoNote}"` : '""'
  )

  const theme = useTheme()

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  if (!imgSrc) return null
  return (
    <Body>
      {removeItem && (
        <ConfirmDeleteDialog
          removeItem={removeItem}
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
        />
      )}
      <Template.Container className='logo-digitization-item-container' {...rest}>
        <Template.Header isDigitization={true} backgroundColor={theme.color.fill.subtle}>
          <ImageWrapper>
            <ResponsiveImage
              alt={logoName}
              aspectRatios={{
                sm: null,
                md: null,
              }}
              src={imgSrc}
              loading={'default'}
              widths={{ unit: 'px', sm: 100, md: 100 }}
              transparentBackground={true}
            />
          </ImageWrapper>
        </Template.Header>
        <Template.Body>
          <TitleRow>
            <Title asTag='h3' data-minicart-item={strings.dtcLogoEmbroideryForm.digitizedLogo}>
              {strings.dtcLogoEmbroideryForm.digitizedLogo}
            </Title>
            {removeItem && (
              <DeleteIconColumn>
                <DeleteIconButton
                  onClick={() => setIsDialogOpen(true)}
                  aria-label={removeItemAriaLabel}
                >
                  <Trash stroke='currentColor' />
                </DeleteIconButton>
              </DeleteIconColumn>
            )}
          </TitleRow>
          <DetailLine $general>{logoLabel}</DetailLine>

          <DetailLine>
            {strings.logoOption.logoColorLabel}:{' '}
            {logoColor === 'BLACK_WHITE'
              ? strings.dtcLogoEmbroideryForm.logoStrings['LOGO_BLACK_WHITE'].title
              : strings.dtcLogoEmbroideryForm.logoStrings['LOGO_COLOR'].title}
          </DetailLine>

          <DetailLine>{logoPlacementLabel}</DetailLine>

          {logoNote && <DetailLine>{logoNoteLabel}</DetailLine>}
          <PriceRow>
            <PriceAndDeleteButton effectivePrice={effectivePrice} fullPrice={fullPrice} />
          </PriceRow>
        </Template.Body>
      </Template.Container>
    </Body>
  )
}

export const Body = styled.div`
  padding: ${o => o.theme.spacing(5)} 0;
`

const ImageWrapper = styled(DigitizationSneakPeekVisual)`
  padding: ${o => o.theme.spacing(0, 3.5)};
`

export const TitleRow = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
`

const Title = styled(TypeStyle.HeadlineExtraExtraSmall)``

export const PriceRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-inline-start: auto;
`

const StyledWarning = styled(Warning)`
  width: 16px;
  height: 16px;
  margin-right: ${o => o.theme.spacing(2.5)};
`

const StyledInformationalDialog = styled(InformationalDialog.Dialog)`
  max-width: 487px;
`

const StyledInformationalDialogHeader = styled(InformationalDialog.Header)`
  .svg {
    stroke-width: 1px important!;
  }
`

const StyledInformationalDialogTitle = styled(InformationalDialog.Title)`
  display: flex;
  align-items: center;
`

const StyledInformationalDialogBody = styled(InformationalDialog.Body)`
  ${TypeStyle.css.bodySmall}
  font-weight: 400;
`

const StyledInformationalDialogControls = styled(InformationalDialog.Controls)`
  flex-direction: row;
  justify-content: space-between;
`

const StyledInformationalDialogButton = styled(InformationalDialog.Button)`
  flex-grow: 1;
`
